import * as React from 'react';

import { cn } from '@rematter/ui-web';
import { LngLatBounds } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import Map, {
  Marker,
  NavigationControl,
  type MapRef,
} from 'react-map-gl/maplibre';

import { mapStyle } from './mapStyle';

export type MapMarker = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  color: string;
  assets?: string[];
};

type MapViewProps = {
  markers: MapMarker[];
  fitPoints?: { lat: number; lng: number }[];
  className?: string;
  children?: React.ReactNode;
};

const US_CENTER = { latitude: 37.1, longitude: -95.7 };
const DEFAULT_ZOOM = 4;
const FIT_BOUNDS_PADDING = 60;

const PIN_SVG = (color: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="22" viewBox="0 0 24 32">` +
  `<path d="M12 0C5.4 0 0 5.4 0 12c0 9 12 20 12 20s12-11 12-20C24 5.4 18.6 0 12 0z" fill="${color}"/>` +
  `<circle cx="12" cy="11" r="5" fill="white"/>` +
  `</svg>`;

function computeBounds(points: { lat: number; lng: number }[]) {
  if (points.length === 0) return undefined;
  if (points.length === 1) {
    return { center: points[0], zoom: 12 };
  }
  const bounds = new LngLatBounds();
  for (const point of points) {
    bounds.extend([point.lng, point.lat]);
  }
  return { bounds };
}

export const MapView = ({
  markers,
  fitPoints,
  className,
  children,
}: MapViewProps) => {
  const mapRef = React.useRef<MapRef>(null);
  const [hoveredMarker, setHoveredMarker] = React.useState<string | null>(null);

  const boundsPoints = fitPoints ?? markers;

  const initialViewState = React.useMemo(() => {
    const computed = computeBounds(boundsPoints);
    if (!computed) {
      return {
        latitude: US_CENTER.latitude,
        longitude: US_CENTER.longitude,
        zoom: DEFAULT_ZOOM,
      };
    }
    if ('center' in computed && computed.center) {
      return {
        latitude: computed.center.lat,
        longitude: computed.center.lng,
        zoom: computed.zoom,
      };
    }
    return {
      bounds: computed.bounds.toArray() as [[number, number], [number, number]],
      fitBoundsOptions: { padding: FIT_BOUNDS_PADDING },
    };
  }, []);

  const fitMapToPoints = React.useCallback(() => {
    const map = mapRef.current;
    if (!map || boundsPoints.length === 0) return;

    const computed = computeBounds(boundsPoints);
    if (!computed) return;

    if ('center' in computed && computed.center) {
      map.jumpTo({
        center: [computed.center.lng, computed.center.lat],
        zoom: computed.zoom,
      });
    } else {
      map.fitBounds(computed.bounds, {
        padding: FIT_BOUNDS_PADDING,
        animate: false,
      });
    }
  }, [boundsPoints]);

  const hasInitialPoints = React.useRef(boundsPoints.length > 0);

  React.useEffect(() => {
    if (hasInitialPoints.current) {
      hasInitialPoints.current = false;
      return;
    }
    fitMapToPoints();
  }, [fitMapToPoints]);

  return (
    <div className={cn('@MapView', cn('h-full w-full', className))}>
      <Map
        ref={mapRef}
        initialViewState={initialViewState}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        maplibreLogo={false}
        attributionControl={false}
        onLoad={() => {
          if (!hasInitialPoints.current) {
            fitMapToPoints();
          }
        }}
      >
        <NavigationControl position="bottom-right" />

        {markers.map((marker) => (
          <Marker
            key={marker.id}
            latitude={marker.lat}
            longitude={marker.lng}
            anchor="bottom"
          >
            <div
              className="relative cursor-pointer"
              onMouseEnter={() => setHoveredMarker(marker.id)}
              onMouseLeave={() => setHoveredMarker(null)}
            >
              <div
                dangerouslySetInnerHTML={{ __html: PIN_SVG(marker.color) }}
              />
              {hoveredMarker === marker.id && (
                <div className="text-background absolute bottom-full left-1/2 mb-1 -translate-x-1/2 rounded bg-foreground px-2 py-1 text-xs whitespace-nowrap shadow-lg">
                  <div className="font-bold">{marker.name}</div>
                  {marker.assets && marker.assets.length > 0 && (
                    <>
                      <hr className="border-background/30 my-1" />
                      <div>{marker.assets.join(', ')}</div>
                    </>
                  )}
                </div>
              )}
            </div>
          </Marker>
        ))}

        {children}
      </Map>
    </div>
  );
};
